import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, Typography } from '@mui/material';
import type { DialogProps } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../color-palette/colors';
import { IconButton } from '../Buttons';
import { StickyActionBar } from '../StickyActionBar';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  primary?: React.ReactElement;
  secondary?: React.ReactElement;
  tertiary?: React.ReactElement;
  message?: React.ReactNode;
  role?: 'dialog' | 'alertdialog';
  closeLabel?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  onExited?: () => void;
}

const SHEET_GUTTER = { xs: 4, md: 6 } as const;

const rise = keyframes`
  from { transform: translateY(var(--sheet-rise)); }
  to { transform: none; }
`;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const dialogSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-end',
    [theme.breakpoints.up('md')]: { alignItems: 'center' },
  },
  [REDUCED_MOTION]: { '& .MuiBackdrop-root, & .MuiDialog-container': { transition: 'none !important' } },
});

const paperSx = (theme: Theme): SystemStyleObject<Theme> => {
  const radius = theme.shape.borderRadius * 4;
  return {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'hidden',
    margin: 0,
    inlineSize: '100%',
    maxInlineSize: '100%',
    maxBlockSize: `calc(100dvh - ${theme.spacing(6)})`,
    borderStartStartRadius: radius,
    borderStartEndRadius: radius,
    borderEndStartRadius: 0,
    borderEndEndRadius: 0,
    '--sheet-rise': theme.spacing(6),
    animation: `${rise} ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(8),
      maxInlineSize: theme.breakpoints.values.sm,
      maxBlockSize: `calc(100% - 2 * ${theme.spacing(8)})`,
      borderRadius: `${radius}px`,
      animation: 'none',
    },
    [REDUCED_MOTION]: { animation: 'none', transition: 'none !important' },
  };
};

const gutterSx = { paddingInline: { xs: SHEET_GUTTER.xs, md: SHEET_GUTTER.md } } as const;

const headerSx = [
  gutterSx,
  { display: 'flex', alignItems: 'center', gap: 2, paddingBlockStart: 5, paddingBlockEnd: 2, flexShrink: 0 },
] as const;
const titleSx = { flex: 1, minInlineSize: 0, color: Neutrals[900], overflowWrap: 'anywhere' } as const;
const bodySx = [
  gutterSx,
  {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: '1 1 auto',
    minBlockSize: 0,
    overflowY: 'auto',
    paddingBlockEnd: 5,
  },
] as const;
const descriptionSx = { color: Neutrals[700] } as const;
const dockSx = [gutterSx, { flexShrink: 0 }] as const;

export const Sheet: React.FC<SheetProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  primary,
  secondary,
  tertiary,
  message,
  role = 'dialog',
  closeLabel,
  initialFocusRef,
  onExited,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const hasDescription = description !== undefined && description !== null && description !== false;
  const hasDock = Boolean(primary || secondary || tertiary) || (message !== undefined && message !== null);

  const handleClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick' && role === 'alertdialog') return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby={titleId}
      aria-describedby={hasDescription ? descriptionId : undefined}
      sx={dialogSx}
      PaperProps={{ role, 'aria-modal': true, sx: paperSx }}
      TransitionProps={{
        onEnter: () => initialFocusRef?.current?.focus(),
        onExited,
      }}
    >
      <Box sx={headerSx}>
        <Typography id={titleId} variant="cardTitle" component="h2" sx={titleSx}>
          {title}
        </Typography>
        {role === 'dialog' && (
          <IconButton label={closeLabel ?? t('common.close', 'Close')} onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Box sx={bodySx}>
        {hasDescription && (
          <Typography id={descriptionId} variant="body" component="p" sx={descriptionSx}>
            {description}
          </Typography>
        )}
        {children}
      </Box>
      {hasDock && (
        <StickyActionBar
          layout="dock"
          placement="surface"
          primary={primary}
          secondary={secondary}
          tertiary={tertiary}
          message={message}
          sx={dockSx}
        />
      )}
    </Dialog>
  );
};
